import { gql } from '@apollo/client';
import {
  ID_NAME_FIELDS,
  COUNTRY_FIELDS,
  LEASE_ANALYTICS_ITEM_FIELDS,
  HISTORICAL_AVERAGE_LEASES_FIELDS,
  HISTORICAL_AVERAGE_AVERAGES_FIELDS,
  SALES_PRICING_ITEMS_FIELDS,
  SALES_PRICING_ROOT_FIELDS,
  PROPERTY_CAROUSEL_FIELDS,
} from 'graphql/common/fragments';

const MARKET_METADATA_COMMON_FIELDS = `
  existingPropertiesData {
    count
    sumSize
    sumSizeMt
  }
  propertiesUnderDevelopmentData {
    count
    sumSize
    sumSizeMt
  }
  averageAge
  averageRentFS
  averageRentFSMonthly
  averageRentNNN
  averageRentNNNMonthly
  developmentInventoryPercentage
  totalBuildingSizes
  availableSpacePercentage
  totalVacancyPercentage
  netAbsorptionLTM
  totalSalesPricing
  averageSalesPricing
  mostRecentLeaseRentType
`;

export const MARKET_QUERY = gql`
  query market($id: ID!) {
    market(id: $id) {
      id
      name
      latitude
      longitude
      country {
        ${COUNTRY_FIELDS}
      }
      propertiesCount
    }
  }
`;

export const SUB_MARKET_QUERY = gql`
  query submarket($id: ID!) {
    submarket(id: $id) {
      id
      name
      market {
        ${ID_NAME_FIELDS}
        latitude
        longitude
        country {
          ${COUNTRY_FIELDS}
        }
      }
    }
  }
`;

export const SUB_MARKET_METADATA_QUERY = gql`
  query submarketMetadata(
    $id: ID!
    $propertyTypeId: Int!
    $marketOptions: MarketAnalyticsOptionsInput!
  ) {
    submarketMetadata(
      id: $id
      propertyTypeId: $propertyTypeId
      marketOptions: $marketOptions
    ) {
      overview {
        country
        state
        marketName
        subMarketName
      }
      ${MARKET_METADATA_COMMON_FIELDS}
    }
  }
`;

export const MARKET_ANALYTICS_METADATA_QUERY = gql`
  query marketAnalyticsMetadata($params: MarketAnalyticsMetadataInput) {
    marketAnalyticsMetadata(params: $params) {
      overview {
        country
        state
        marketName
      }
      ${MARKET_METADATA_COMMON_FIELDS}
    }
  }
`;

export const MARKETS_QUERY = gql`
  query markets($search: MarketSearchInput, $includePropertiesCount: Boolean) {
    markets(search: $search, includePropertiesCount: $includePropertiesCount) {
      id
      name
      country {
        ${COUNTRY_FIELDS}
      }
      propertiesCount
    }
  }
`;

export const SUB_MARKETS_QUERY = gql`
  query submarkets($search: SubmarketSearchInput) {
    submarkets(search: $search) {
      id
      name
      shapeGroup
      market {
        ${ID_NAME_FIELDS}
        country {
          ${COUNTRY_FIELDS}
        }
      }
    }
  }
`;

export const MICRO_MARKETS_QUERY = gql`
  query micromarkets($search: MicromarketSearchInput) {
    micromarkets(search: $search) {
      id
      name
      submarket {
        ${ID_NAME_FIELDS}
      }
    }
  }
`;

export const MARKETS_BOUNDARIES_QUERY = gql`
  query submarketsBoundaries($search: SubmarketBoundariesInput) {
    submarketsBoundaries(search: $search) {
      id
      name
      geometry {
        type
        coordinates
      }
    }
  }
`;

export const MARKETS_ANALYTICS_PROPERTIES_CAROUSEL = gql`
  query marketAnalyticsPropertiesCarousel(
    $params: MarketAnalyticsPropertiesCarouselInput
  ) {
    marketAnalyticsPropertiesCarousel(params: $params) {
      ${PROPERTY_CAROUSEL_FIELDS}
    }
  }
`;

export const PROPERTY_TYPES_BY_MARKET = gql`
  query propertyTypesOfMarket($search: PropertyTypesOfMarketInput!) {
    propertyTypesOfMarket(search: $search) {
      items {
        propertyTypeId
        propertyTypeName
      }
    }
  }
`;

export const MARKET_ANALYTICS_HISTORICAL_LEASING = gql`
  query marketAnalyticsHistoricalLeasing(
    $params: MarketAnalyticsHistoricalLeasingInput
  ) {
    marketAnalyticsHistoricalLeasing(params: $params) {
      historicalAverage {
        averageType
        totalAverage
        averages {
          ${HISTORICAL_AVERAGE_AVERAGES_FIELDS}
        }
        leases {
          ${HISTORICAL_AVERAGE_LEASES_FIELDS}
        }
      }
      historicalLeases {
        rentType
        items {
          ${LEASE_ANALYTICS_ITEM_FIELDS}
          baseRentFullServiceEquiv
          baseRentNNNEquiv
        }
      }
    }
  }
`;

export const MARKET_SALES_PRICING_QUERY = gql`
  query marketAnalyticsSalesPricing(
    $params: MarketAnalyticsSalesPricingInput!
  ) {
    marketAnalyticsSalesPricing(params: $params) {
      ${SALES_PRICING_ROOT_FIELDS}
      items {
        ${SALES_PRICING_ITEMS_FIELDS}
      }
    }
  }
`;

export const MARKET_DEVELOPMENT_GRAPH_QUERY = gql`
  query marketAnalyticsDevelopmentTimeline(
    $params: MarketAnalyticsDevelopmentTimelineInput
  ) {
    marketAnalyticsDevelopmentTimeline(params: $params) {
      years {
        builtYear
        totalBuildingSize
        totalBuildingSizeMt
        topItems
      }
    }
  }
`;
